// Statistics aggregator.
//
// One aggregator instance == one analysis segment (the whole log, a Mythic+ run
// or a single boss encounter). Several of them are fed by the same event stream,
// which is why every counter here is incremental: appending new events to a file
// never requires recomputing what came before.
//
// Pet damage is stored under the pet's own GUID and merged into its owner when
// the report is built. Ownership is often learned later than the first pet hit,
// and merging late keeps the result identical either way.

const MAX_TIMELINE_BUCKETS = 1200

export function createTimeline(startTs, bucketMs = 1000) {
    return {
        startTs,
        bucketMs,
        length: 0,
        series: new Map(),
        totalDamage: [],
        totalHealing: [],
        totalTaken: [],
    }
}

function halveSeries(values) {
    const next = []
    for (let index = 0; index < values.length; index += 2) {
        next.push((values[index] || 0) + (values[index + 1] || 0))
    }
    return next
}

function downsample(timeline) {
    timeline.bucketMs *= 2
    timeline.totalDamage = halveSeries(timeline.totalDamage)
    timeline.totalHealing = halveSeries(timeline.totalHealing)
    timeline.totalTaken = halveSeries(timeline.totalTaken)
    timeline.series.forEach((entry) => {
        entry.damage = halveSeries(entry.damage)
        entry.healing = halveSeries(entry.healing)
        entry.taken = halveSeries(entry.taken)
    })
    timeline.length = Math.ceil(timeline.length / 2)
}

function timelineSlot(timeline, ts) {
    let index = Math.floor((ts - timeline.startTs) / timeline.bucketMs)
    if (index < 0) index = 0
    while (index >= MAX_TIMELINE_BUCKETS) {
        downsample(timeline)
        index = Math.floor((ts - timeline.startTs) / timeline.bucketMs)
    }
    if (index + 1 > timeline.length) timeline.length = index + 1
    return index
}

function addToSeries(values, index, amount) {
    values[index] = (values[index] || 0) + amount
}

export function timelineAdd(timeline, guid, kind, ts, amount) {
    if (!amount) return
    const index = timelineSlot(timeline, ts)

    let entry = timeline.series.get(guid)
    if (!entry) {
        entry = { damage: [], healing: [], taken: [] }
        timeline.series.set(guid, entry)
    }

    addToSeries(entry[kind], index, amount)
    if (kind === 'damage') addToSeries(timeline.totalDamage, index, amount)
    else if (kind === 'healing') addToSeries(timeline.totalHealing, index, amount)
    else addToSeries(timeline.totalTaken, index, amount)
}

function createActorStats(guid) {
    return {
        guid,
        firstTs: 0,
        lastTs: 0,
        activeSeconds: 0,
        lastActiveSecond: -1,

        damage: 0,
        damageHits: 0,
        damageCrits: 0,
        overkill: 0,
        friendlyFire: 0,

        healing: 0,
        overhealing: 0,
        healingHits: 0,
        healingCrits: 0,
        healingAbsorbed: 0,
        healingReceived: 0,
        absorbDone: 0,

        damageTaken: 0,
        damageTakenAbsorbed: 0,
        damageTakenBlocked: 0,
        damageTakenHits: 0,

        casts: 0,
        interrupts: 0,
        dispels: 0,
        resurrects: 0,
        deaths: 0,
        killingBlows: 0,
        misses: 0,

        damageBySpell: new Map(),
        damageByTarget: new Map(),
        healBySpell: new Map(),
        healByTarget: new Map(),
        takenBySpell: new Map(),
        takenBySource: new Map(),
        castsBySpell: new Map(),
        interruptedSpells: new Map(),
        dispelledSpells: new Map(),
        auraUptime: new Map(),
        missTypes: new Map(),
    }
}

function touch(stats, ts) {
    if (!stats.firstTs) stats.firstTs = ts
    stats.lastTs = ts
}

function markActive(stats, ts) {
    const second = Math.floor(ts / 1000)
    if (second !== stats.lastActiveSecond) {
        stats.lastActiveSecond = second
        stats.activeSeconds += 1
    }
}

function bumpSpell(map, spellId, name, school, amount, options = {}) {
    let entry = map.get(spellId)
    if (!entry) {
        entry = {
            spellId,
            name: name || ('Spell #' + spellId),
            school: school || 0,
            amount: 0,
            hits: 0,
            crits: 0,
            overheal: 0,
            absorbed: 0,
            overkill: 0,
            min: 0,
            max: 0,
            sourceName: options.sourceName || '',
        }
        map.set(spellId, entry)
    }

    entry.amount += amount
    entry.hits += 1
    if (options.critical) entry.crits += 1
    if (options.overheal) entry.overheal += options.overheal
    if (options.absorbed) entry.absorbed += options.absorbed
    if (options.overkill > 0) entry.overkill += options.overkill
    if (amount > entry.max) entry.max = amount
    if (!entry.min || (amount > 0 && amount < entry.min)) entry.min = amount
    if (options.sourceName && !entry.sourceName) entry.sourceName = options.sourceName
    return entry
}

function bumpUnit(map, key, name, amount, extra = 0) {
    let entry = map.get(key)
    if (!entry) {
        entry = { key, name: name || 'Unknown', amount: 0, hits: 0, extra: 0 }
        map.set(key, entry)
    }
    entry.amount += amount
    entry.hits += 1
    if (extra) entry.extra += extra
    if (name && entry.name === 'Unknown') entry.name = name
    return entry
}

function bumpCount(map, id, name) {
    let entry = map.get(id)
    if (!entry) {
        entry = { id, name: name || ('Spell #' + id), count: 0 }
        map.set(id, entry)
    }
    entry.count += 1
    return entry
}

// Enemies are grouped by npc id so that the twenty copies of one trash mob show
// up as a single row instead of twenty spawn GUIDs.
export function unitKey(actor, guid) {
    if (!actor) return guid || 'unknown'
    if (actor.isPlayer) return actor.guid
    if (actor.npcId) return 'npc:' + actor.npcId
    return actor.guid
}

export function createSegment(options) {
    const startTs = options.startTs || 0

    return {
        key: options.key,
        type: options.type,
        name: options.name || '',
        startTs,
        endTs: 0,
        lastTs: startTs,
        meta: options.meta || {},
        roster: new Map(),
        actors: new Map(),
        timeline: createTimeline(startTs, options.bucketMs || 1000),
        deaths: [],
        enemyDeaths: 0,
        activeSeconds: 0,
        lastActiveSecond: -1,
        eventCount: 0,
        totals: {
            damage: 0,
            healing: 0,
            overhealing: 0,
            absorbDone: 0,
            damageTaken: 0,
            deaths: 0,
            interrupts: 0,
            dispels: 0,
            casts: 0,
        },
        report: null,
    }
}

// Segments are plain data on purpose: they are structured-cloned into IndexedDB
// and between the worker and the page, which functions would break.
export function segmentStats(segment, guid) {
    let entry = segment.actors.get(guid)
    if (!entry) {
        entry = createActorStats(guid)
        segment.actors.set(guid, entry)
    }
    return entry
}

export function markSegmentActivity(segment, ts) {
    const second = Math.floor(ts / 1000)
    if (second !== segment.lastActiveSecond) {
        segment.lastActiveSecond = second
        segment.activeSeconds += 1
    }
}

// Applies one parsed event to one segment. `context` carries the entity
// registry so that the aggregator can resolve names and unit kinds without
// duplicating that state per segment.
export function applyEvent(segment, event, context) {
    const { registry } = context
    const ts = event.ts
    segment.eventCount += 1
    if (ts > segment.lastTs) segment.lastTs = ts

    switch (event.category) {
        case 'damage': {
            const amount = event.amount
            const source = registry.get(event.sourceGuid)
            const dest = registry.get(event.destGuid)

            if (amount > 0) {
                markSegmentActivity(segment, ts)
                segment.totals.damage += amount
            }

            if (source && amount > 0) {
                const stats = segmentStats(segment, source.guid)
                touch(stats, ts)
                markActive(stats, ts)
                stats.damage += amount
                stats.damageHits += 1
                if (event.critical) stats.damageCrits += 1
                if (event.overkill > 0) stats.overkill += event.overkill
                if (dest && dest.isFriendly && dest.isPlayer && source.isFriendly) {
                    stats.friendlyFire += amount
                }

                bumpSpell(stats.damageBySpell, event.spellId, event.spellName, event.spellSchool || event.school, amount, {
                    critical: event.critical,
                    overkill: event.overkill,
                })
                if (dest) bumpUnit(stats.damageByTarget, unitKey(dest, event.destGuid), dest.name, amount)
                timelineAdd(segment.timeline, source.guid, 'damage', ts, amount)
            }

            if (dest && dest.isPlayer) {
                const stats = segmentStats(segment, dest.guid)
                touch(stats, ts)
                stats.damageTaken += amount
                stats.damageTakenHits += 1
                stats.damageTakenBlocked += event.blocked
                segment.totals.damageTaken += amount

                const sourceLabel = event.environmentalType
                    ? event.environmentalType
                    : (source ? source.name : event.sourceName || 'Unknown')
                bumpSpell(stats.takenBySpell, event.spellId || -1, event.spellName || event.environmentalType || 'Melee', event.spellSchool || event.school, amount, {
                    critical: event.critical,
                    absorbed: event.absorbed,
                    sourceName: sourceLabel,
                })
                bumpUnit(stats.takenBySource, source ? unitKey(source, event.sourceGuid) : 'env:' + sourceLabel, sourceLabel, amount)
                timelineAdd(segment.timeline, dest.guid, 'taken', ts, amount)
            }
            break
        }

        case 'heal': {
            const total = event.amount
            const overheal = event.overheal
            const effective = Math.max(0, total - overheal)
            const source = registry.get(event.sourceGuid)
            const dest = registry.get(event.destGuid)

            if (total > 0) markSegmentActivity(segment, ts)
            segment.totals.healing += effective
            segment.totals.overhealing += overheal

            if (source) {
                const stats = segmentStats(segment, source.guid)
                touch(stats, ts)
                if (effective > 0) markActive(stats, ts)
                stats.healing += effective
                stats.overhealing += overheal
                stats.healingHits += 1
                stats.healingAbsorbed += event.absorbed
                if (event.critical) stats.healingCrits += 1

                bumpSpell(stats.healBySpell, event.spellId, event.spellName, event.spellSchool, effective, {
                    critical: event.critical,
                    overheal,
                    absorbed: event.absorbed,
                })
                if (dest) bumpUnit(stats.healByTarget, unitKey(dest, event.destGuid), dest.name, effective, overheal)
                timelineAdd(segment.timeline, source.guid, 'healing', ts, effective)
            }

            if (dest && dest.isPlayer) {
                const stats = segmentStats(segment, dest.guid)
                touch(stats, ts)
                stats.healingReceived += effective
            }
            break
        }

        case 'absorbed': {
            // SPELL_ABSORBED: damage prevented by a shield. It is intentionally
            // not added to damage taken, because the damage event already
            // reports the post-absorb amount.
            const amount = event.amount
            if (amount <= 0) break

            const caster = registry.get(event.absorbCasterGuid)
            const dest = registry.get(event.destGuid)

            if (caster) {
                const stats = segmentStats(segment, caster.guid)
                touch(stats, ts)
                stats.absorbDone += amount
                segment.totals.absorbDone += amount
                bumpSpell(stats.healBySpell, event.extraSpellId, event.extraSpellName, event.extraSpellSchool, 0, {
                    absorbed: amount,
                })
            }

            if (dest && dest.isPlayer) {
                const stats = segmentStats(segment, dest.guid)
                touch(stats, ts)
                stats.damageTakenAbsorbed += amount
            }
            break
        }

        case 'healAbsorbed': {
            const healer = registry.get(event.healerGuid)
            if (healer && event.amount > 0) {
                const stats = segmentStats(segment, healer.guid)
                stats.healingAbsorbed += event.amount
            }
            break
        }

        case 'miss': {
            const dest = registry.get(event.destGuid)
            if (dest && dest.isPlayer) {
                const stats = segmentStats(segment, dest.guid)
                touch(stats, ts)
                stats.misses += 1
                bumpCount(stats.missTypes, event.missType || 'MISS', event.missType || 'MISS')
            }
            break
        }

        case 'cast': {
            if (event.name !== 'SPELL_CAST_SUCCESS') break
            const source = registry.get(event.sourceGuid)
            if (!source) break
            const stats = segmentStats(segment, source.guid)
            touch(stats, ts)
            stats.casts += 1
            segment.totals.casts += 1
            bumpCount(stats.castsBySpell, event.spellId, event.spellName)
            break
        }

        case 'utility': {
            const source = registry.get(event.sourceGuid)
            if (!source) break
            const stats = segmentStats(segment, source.guid)
            touch(stats, ts)
            if (event.name === 'SPELL_INTERRUPT') {
                stats.interrupts += 1
                segment.totals.interrupts += 1
                bumpCount(stats.interruptedSpells, event.extraSpellId, event.extraSpellName)
            } else if (event.name === 'SPELL_DISPEL' || event.name === 'SPELL_STOLEN') {
                stats.dispels += 1
                segment.totals.dispels += 1
                bumpCount(stats.dispelledSpells, event.extraSpellId, event.extraSpellName)
            }
            break
        }

        case 'resurrect': {
            const source = registry.get(event.sourceGuid)
            if (source) segmentStats(segment, source.guid).resurrects += 1
            break
        }

        case 'kill': {
            const source = registry.get(event.sourceGuid)
            if (source) segmentStats(segment, source.guid).killingBlows += 1
            break
        }

        default:
            break
    }
}

// Aura uptime is tracked for group members only: every unit in the log would
// mean thousands of live aura entries for no analytical gain.
export function applyAura(segment, event, actor) {
    const stats = segmentStats(segment, actor.guid)
    let entry = stats.auraUptime.get(event.spellId)
    if (!entry) {
        entry = {
            spellId: event.spellId,
            name: event.spellName || ('Spell #' + event.spellId),
            auraType: event.auraType || '',
            applications: 0,
            totalMs: 0,
            activeSince: 0,
            maxStacks: 0,
        }
        stats.auraUptime.set(event.spellId, entry)
    }

    if (event.name === 'SPELL_AURA_APPLIED' || event.name === 'SPELL_AURA_REFRESH') {
        if (!entry.activeSince) {
            entry.activeSince = event.ts
            entry.applications += 1
        }
        if (event.stacks > entry.maxStacks) entry.maxStacks = event.stacks
    } else if (event.name === 'SPELL_AURA_APPLIED_DOSE') {
        if (!entry.activeSince) {
            entry.activeSince = event.ts
            entry.applications += 1
        }
        if (event.stacks > entry.maxStacks) entry.maxStacks = event.stacks
    } else if (event.name === 'SPELL_AURA_REMOVED') {
        if (entry.activeSince) {
            entry.totalMs += Math.max(0, event.ts - entry.activeSince)
            entry.activeSince = 0
        }
    }
}

export function closeOpenAuras(segment, endTs) {
    segment.actors.forEach((stats) => {
        stats.auraUptime.forEach((entry) => {
            if (entry.activeSince) {
                entry.totalMs += Math.max(0, endTs - entry.activeSince)
                entry.activeSince = 0
            }
        })
    })
}
